import { useCallback, useEffect, useRef, useState } from "react";

import type {
  ActionReceipt,
  CanonicalError,
  ChatView,
  WorkspaceView,
} from "./api/contracts";
import {
  DevChatApiClient,
  isUnauthorized,
  toCanonicalError,
} from "./api/client";
import { Composer } from "./components/Composer";
import { Login } from "./components/Login";
import { Sidebar } from "./components/Sidebar";
import { CompactStatus, type ConnectionState } from "./components/Status";
import { Timeline } from "./components/Timeline";
import {
  compareCursors,
  configurationScopeWasRemoved,
  type ConfigurationInvalidationScope,
  failClosedAfterRefresh,
  failClosedWorkspace,
  isConfigurationInvalidationError,
  latestCanonicalStatus,
  mergeChatPage,
  mergeTimelinePage,
  mergeTimelineDelta,
  mergeWorkspaceSnapshot,
  normalizeWorkspace,
  reconcileModelRegistry,
} from "./state/workspace";

const api = new DevChatApiClient();
const MODEL_REGISTRY_POLL_INTERVAL_MS = 5_000;

type AppPhase = "booting" | "login" | "ready" | "unavailable";

interface StreamSubscription {
  chatId: string;
  after: string;
  generation: number;
}

interface CreateAttempt {
  clientActionId: string;
  modelId: string;
  createdChatId?: string;
}

interface MessageAttempt {
  chatId: string;
  clientActionId: string;
  text: string;
}

interface PurchaseAttempt {
  chatId: string;
  clientActionId: string;
  offerId: string;
  receipt?: ActionReceipt;
}

export default function App() {
  const [phase, setPhase] = useState<AppPhase>("booting");
  const [workspace, setWorkspace] = useState<WorkspaceView | null>(null);
  const workspaceRef = useRef<WorkspaceView | null>(null);
  const workspaceRevisionRef = useRef(0);
  const registryReadinessRefreshPendingRef = useRef(false);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [loginPending, setLoginPending] = useState(false);
  const [loginError, setLoginError] = useState<CanonicalError | null>(null);
  const [pageError, setPageError] = useState<CanonicalError | null>(null);
  const [streamError, setStreamError] = useState<CanonicalError | null>(null);
  const [connection, setConnection] = useState<ConnectionState>("connecting");
  const [subscription, setSubscription] = useState<StreamSubscription | null>(null);
  const streamGenerationRef = useRef(0);
  const [creating, setCreating] = useState(false);
  const creatingRef = useRef(false);
  const createAttemptsRef = useRef(new Map<string, CreateAttempt>());
  const [loadingChat, setLoadingChat] = useState(false);
  const loadingChatRef = useRef(false);
  const [loadingMoreChats, setLoadingMoreChats] = useState(false);
  const loadingMoreChatsRef = useRef(false);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const loadingEarlierRef = useRef(false);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const messageAttemptsRef = useRef(new Map<string, MessageAttempt>());
  const [pendingOfferId, setPendingOfferId] = useState<string | null>(null);
  const purchasePendingRef = useRef(false);
  const purchaseAttemptsRef = useRef(new Map<string, PurchaseAttempt>());

  const commitWorkspace = useCallback((next: WorkspaceView, preferredModelId?: string): void => {
    const normalized = mergeWorkspaceSnapshot(workspaceRef.current, next);
    workspaceRevisionRef.current += 1;
    workspaceRef.current = normalized;
    setWorkspace(normalized);
    setSelectedModelId((current) => chooseModelId(normalized, preferredModelId, current));
    setPageError(null);
    setStreamError(null);
    streamGenerationRef.current += 1;
    setSubscription(
      normalized.activeChat === null
        ? null
        : {
            chatId: normalized.activeChat.chatId,
            after: normalized.cursor,
            generation: streamGenerationRef.current,
          },
    );
  }, []);

  const openWorkspace = useCallback(async (
    chatId?: string,
    preferredModelId?: string,
  ): Promise<WorkspaceView> => {
    const next = await api.loadWorkspace(chatId);
    if (chatId !== undefined && next.activeChat?.chatId !== chatId) {
      throw new Error("ACTIVE_CHAT_RESPONSE_MISMATCH");
    }
    commitWorkspace(next, preferredModelId);
    return next;
  }, [commitWorkspace]);

  const reconcileConfigurationInvalidation = useCallback(async (
    error: CanonicalError,
    scope: ConfigurationInvalidationScope,
  ): Promise<void> => {
    const current = workspaceRef.current;
    if (current === null) return;

    const invalidated = failClosedWorkspace(current, error.code, scope);
    workspaceRevisionRef.current += 1;
    const invalidationRevision = workspaceRevisionRef.current;
    streamGenerationRef.current += 1;
    workspaceRef.current = invalidated;
    setWorkspace(invalidated);
    setSelectedModelId((selected) =>
      chooseModelId(invalidated, undefined, selected),
    );
    setSubscription(null);
    setConnection("connected");
    setStreamError(null);
    setPageError(error);

    try {
      const refreshed = await api.loadWorkspace();
      if (workspaceRevisionRef.current !== invalidationRevision) return;
      const reconciled = failClosedAfterRefresh(
        normalizeWorkspace(refreshed),
        error.code,
        scope,
      );
      const invalidationStillApplies = configurationScopeWasRemoved(
        reconciled,
        error.code,
        scope,
      );
      commitWorkspace(reconciled);
      if (invalidationStillApplies) setPageError(error);
    } catch (refreshError: unknown) {
      if (workspaceRevisionRef.current !== invalidationRevision) return;
      if (isUnauthorized(refreshError)) {
        workspaceRevisionRef.current += 1;
        workspaceRef.current = null;
        setWorkspace(null);
        setSubscription(null);
        setLoginError(toCanonicalError(refreshError));
        setPhase("login");
        return;
      }
      setPageError(toCanonicalError(refreshError));
    }
  }, [commitWorkspace]);

  useEffect(() => {
    let active = true;

    async function boot(): Promise<void> {
      try {
        const next = await api.loadWorkspace();
        if (!active) return;
        commitWorkspace(next);
        setPhase("ready");
      } catch (error: unknown) {
        if (!active) return;
        if (isUnauthorized(error)) {
          setPhase("login");
          return;
        }
        setPageError(toCanonicalError(error));
        setPhase("unavailable");
      }
    }

    void boot();
    return () => {
      active = false;
    };
  }, [commitWorkspace]);

  useEffect(() => {
    if (phase !== "ready") return;

    let active = true;
    let timer: number | null = null;
    const isPollingActive = (): boolean => active;

    const commitWithoutStreamRestart = (next: WorkspaceView): void => {
      workspaceRevisionRef.current += 1;
      workspaceRef.current = next;
      setWorkspace(next);
      setSelectedModelId((selected) =>
        chooseModelId(next, undefined, selected),
      );
    };

    const failClosed = (error: unknown): void => {
      const canonical = toCanonicalError(error);
      active = false;
      registryReadinessRefreshPendingRef.current = false;
      workspaceRevisionRef.current += 1;
      streamGenerationRef.current += 1;
      workspaceRef.current = null;
      setWorkspace(null);
      setSubscription(null);
      setConnection("connected");
      setStreamError(null);
      if (isUnauthorized(error)) {
        setLoginError(canonical);
        setPhase("login");
        return;
      }
      setPageError(canonical);
      setPhase("unavailable");
    };

    const poll = async (): Promise<void> => {
      try {
        if (!window.navigator.onLine) return;
        const registry = await api.loadModelRegistry();
        if (!active) return;
        const current = workspaceRef.current;
        if (current === null) return;
        const reconciliation = reconcileModelRegistry(current, registry);
        if (reconciliation.changed) {
          commitWithoutStreamRestart(reconciliation.workspace);
          if (reconciliation.readinessChanged) {
            registryReadinessRefreshPendingRef.current = true;
          }
          if (reconciliation.activeModelRemoved) {
            streamGenerationRef.current += 1;
            setSubscription(null);
            setConnection("connected");
            setStreamError(null);
          }
        }

        if (registryReadinessRefreshPendingRef.current) {
          const beforeRefresh = workspaceRef.current;
          if (beforeRefresh === null) return;
          const requestedChatId = beforeRefresh.activeChat?.chatId;
          const refreshed = await api.loadWorkspace(requestedChatId);
          if (!isPollingActive()) return;
          if (
            requestedChatId !== undefined &&
            refreshed.activeChat?.chatId !== requestedChatId
          ) {
            throw new Error("REGISTRY_REFRESH_CHAT_SCOPE_MISMATCH");
          }
          const latest = workspaceRef.current;
          if (
            latest === null ||
            latest.activeChat?.chatId !== requestedChatId
          ) {
            return;
          }
          commitWithoutStreamRestart(
            mergeWorkspaceSnapshot(latest, refreshed),
          );
          registryReadinessRefreshPendingRef.current = false;
        }
      } catch (error: unknown) {
        if (!window.navigator.onLine) return;
        if (active) failClosed(error);
      } finally {
        if (active) {
          timer = window.setTimeout(
            () => void poll(),
            MODEL_REGISTRY_POLL_INTERVAL_MS,
          );
        }
      }
    };

    timer = window.setTimeout(
      () => void poll(),
      MODEL_REGISTRY_POLL_INTERVAL_MS,
    );
    return () => {
      active = false;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [phase]);

  const activeChat = visibleActiveChat(workspace, selectedModelId);
  const activeChatId = activeChat?.chatId ?? null;
  useEffect(() => {
    if (
      subscription === null ||
      activeChatId === null ||
      subscription.chatId !== activeChatId
    ) {
      setConnection("connected");
      return;
    }

    setConnection("connecting");
    setStreamError(null);
    const isCurrentSubscription = (): boolean =>
      streamGenerationRef.current === subscription.generation &&
      workspaceRef.current?.activeChat?.chatId === subscription.chatId;
    return api.watchTimeline(subscription.chatId, subscription.after, {
      onOpen: () => {
        if (isCurrentSubscription()) setConnection("connected");
      },
      onTransportError: () => {
        if (isCurrentSubscription()) setConnection("reconnecting");
      },
      onTerminalError: (error) => {
        if (!isCurrentSubscription()) return;
        if (isSessionExpiredError(error)) {
          workspaceRevisionRef.current += 1;
          workspaceRef.current = null;
          setWorkspace(null);
          setSubscription(null);
          setLoginError(error);
          setPhase("login");
          return;
        }
        const current = workspaceRef.current;
        if (
          current?.activeChat !== null &&
          current?.activeChat !== undefined &&
          isConfigurationInvalidationError(error)
        ) {
          void reconcileConfigurationInvalidation(error, {
            chatId: subscription.chatId,
            modelId: current.activeChat.modelId,
          });
          return;
        }
        setStreamError(error);
        setConnection("reconnecting");
      },
      onDelta: (delta) => {
        if (!isCurrentSubscription()) return;
        const current = workspaceRef.current;
        if (current === null) {
          throw new Error("TIMELINE_WITHOUT_WORKSPACE");
        }
        const merged = mergeTimelineDelta(current, delta);
        workspaceRef.current = merged;
        setWorkspace(merged);
      },
    });
  }, [activeChatId, reconcileConfigurationInvalidation, subscription]);

  async function login(token: string): Promise<void> {
    setLoginPending(true);
    setLoginError(null);
    try {
      await api.login(token);
      await openWorkspace();
      setPhase("ready");
    } catch (error: unknown) {
      setLoginError(toCanonicalError(error));
    } finally {
      setLoginPending(false);
    }
  }

  async function retryBoot(): Promise<void> {
    setPhase("booting");
    setPageError(null);
    try {
      await openWorkspace();
      setPhase("ready");
    } catch (error: unknown) {
      if (isUnauthorized(error)) {
        setPhase("login");
      } else {
        setPageError(toCanonicalError(error));
        setPhase("unavailable");
      }
    }
  }

  function selectModel(modelId: string): void {
    setSelectedModelId(modelId);
    setPageError(null);
  }

  async function selectChat(chatId: string): Promise<void> {
    if (loadingChatRef.current || creatingRef.current) return;
    loadingChatRef.current = true;
    setLoadingChat(true);
    setPageError(null);
    try {
      const next = await openWorkspace(chatId);
      const modelId = next.activeChat?.modelId;
      if (modelId === undefined) throw new Error("ACTIVE_CHAT_MODEL_MISSING");
      setSelectedModelId(modelId);
    } catch (error: unknown) {
      handleAuthenticatedError(error, { chatId, modelId: selectedModelId });
    } finally {
      loadingChatRef.current = false;
      setLoadingChat(false);
    }
  }

  async function loadMoreChats(): Promise<void> {
    const current = workspaceRef.current;
    if (
      !current?.chatsNextBefore ||
      loadingMoreChatsRef.current
    ) return;
    loadingMoreChatsRef.current = true;
    setLoadingMoreChats(true);
    setPageError(null);
    try {
      const page = await api.loadWorkspace(undefined, {
        chatsBefore: current.chatsNextBefore,
      });
      const latest = workspaceRef.current;
      if (latest === null) return;
      const merged = mergeChatPage(latest, page);
      workspaceRevisionRef.current += 1;
      workspaceRef.current = merged;
      setWorkspace(merged);
      setSelectedModelId((selected) => chooseModelId(merged, undefined, selected));
    } catch (error: unknown) {
      handleAuthenticatedError(error);
    } finally {
      loadingMoreChatsRef.current = false;
      setLoadingMoreChats(false);
    }
  }

  async function loadEarlierMessages(): Promise<void> {
    const current = workspaceRef.current;
    const chat = current?.activeChat;
    if (
      !chat?.timelineNextBefore ||
      loadingEarlierRef.current
    ) return;
    loadingEarlierRef.current = true;
    setLoadingEarlier(true);
    setPageError(null);
    try {
      const page = await api.loadWorkspace(chat.chatId, {
        timelineBefore: chat.timelineNextBefore,
      });
      const latest = workspaceRef.current;
      if (latest?.activeChat?.chatId !== chat.chatId) return;
      const merged = mergeTimelinePage(latest, page);
      workspaceRevisionRef.current += 1;
      workspaceRef.current = merged;
      setWorkspace(merged);
    } catch (error: unknown) {
      handleAuthenticatedError(error, {
        chatId: chat.chatId,
        modelId: chat.modelId,
      });
    } finally {
      loadingEarlierRef.current = false;
      setLoadingEarlier(false);
    }
  }

  async function createChat(): Promise<void> {
    if (
      selectedModelId === null ||
      workspaceRef.current?.models.find(
        (model) => model.modelId === selectedModelId,
      )?.readOnly !== false ||
      creatingRef.current ||
      loadingChatRef.current
    ) return;
    creatingRef.current = true;
    const modelId = selectedModelId;
    const attempt = createAttemptsRef.current.get(modelId) ?? {
      modelId,
      clientActionId: crypto.randomUUID(),
    };
    createAttemptsRef.current.set(modelId, attempt);
    setCreating(true);
    setPageError(null);
    try {
      let createdChatId = attempt.createdChatId;
      if (createdChatId === undefined) {
        const created = await api.createChat(modelId, attempt.clientActionId);
        if (created.modelId !== modelId) {
          throw new Error("CREATED_CHAT_MODEL_MISMATCH");
        }
        createdChatId = created.chatId;
        attempt.createdChatId = createdChatId;
      }
      await openWorkspace(createdChatId, modelId);
      createAttemptsRef.current.delete(modelId);
    } catch (error: unknown) {
      const canonical = handleAuthenticatedError(error, {
        chatId: attempt.createdChatId ?? null,
        modelId,
      });
      if (
        attempt.createdChatId === undefined &&
        !canonical.safeToRetryWithSameIdempotencyKey
      ) {
        createAttemptsRef.current.delete(modelId);
      }
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  }

  async function sendMessage(text: string): Promise<boolean> {
    if (activeChat === null || activeChat.readOnly || sendingRef.current) return false;
    sendingRef.current = true;
    const { chatId, modelId } = activeChat;
    const attemptKey = scopedAttemptKey(chatId, text);
    const attempt = messageAttemptsRef.current.get(attemptKey) ?? {
      chatId,
      clientActionId: crypto.randomUUID(),
      text,
    };
    messageAttemptsRef.current.set(attemptKey, attempt);
    setSending(true);
    setPageError(null);
    try {
      const receipt = await api.act(chatId, {
        kind: "message.v1",
        clientActionId: attempt.clientActionId,
        text,
      });
      if (
        receipt.chatId !== chatId ||
        receipt.clientActionId !== attempt.clientActionId
      ) {
        throw new Error("ACTION_RECEIPT_CHAT_MISMATCH");
      }
      messageAttemptsRef.current.delete(attemptKey);
      return true;
    } catch (error: unknown) {
      const canonical = handleAuthenticatedError(error, {
        chatId,
        modelId,
      });
      if (!canonical.safeToRetryWithSameIdempotencyKey) {
        messageAttemptsRef.current.delete(attemptKey);
      }
      return false;
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }

  async function purchase(offerId: string): Promise<void> {
    if (activeChat === null || activeChat.readOnly || purchasePendingRef.current) return;
    purchasePendingRef.current = true;
    const chatId = activeChat.chatId;
    const attemptKey = scopedAttemptKey(chatId, offerId);
    const attempt = purchaseAttemptsRef.current.get(attemptKey) ?? {
      chatId,
      clientActionId: crypto.randomUUID(),
      offerId,
    };
    purchaseAttemptsRef.current.set(attemptKey, attempt);
    setPendingOfferId(offerId);
    setPageError(null);
    try {
      let receipt = attempt.receipt;
      if (receipt === undefined) {
        receipt = await api.act(chatId, {
          kind: "purchase.v1",
          clientActionId: attempt.clientActionId,
          offerId,
        });
        if (
          receipt.chatId !== chatId ||
          receipt.clientActionId !== attempt.clientActionId
        ) {
          throw new Error("ACTION_RECEIPT_CHAT_MISMATCH");
        }
        attempt.receipt = receipt;
      }
      const refreshed = await api.loadWorkspace(chatId);
      if (
        refreshed.activeChat?.chatId !== chatId ||
        refreshed.activeChat.modelId !== activeChat.modelId
      ) {
        throw new Error("PURCHASE_SNAPSHOT_CHAT_MISMATCH");
      }
      if (compareCursors(refreshed.cursor, receipt.timelineCursor) < 0) {
        throw new Error("PURCHASE_SNAPSHOT_BEHIND_RECEIPT");
      }
      commitWorkspace(refreshed, activeChat.modelId);
      purchaseAttemptsRef.current.delete(attemptKey);
    } catch (error: unknown) {
      const canonical = handleAuthenticatedError(error);
      if (
        attempt.receipt === undefined &&
        !canonical.safeToRetryWithSameIdempotencyKey
      ) {
        purchaseAttemptsRef.current.delete(attemptKey);
      }
    } finally {
      purchasePendingRef.current = false;
      setPendingOfferId(null);
    }
  }

  function handleAuthenticatedError(
    error: unknown,
    scope?: { chatId: string | null; modelId: string | null },
  ): CanonicalError {
    const canonical = toCanonicalError(error);
    if (isUnauthorized(error)) {
      workspaceRef.current = null;
      setWorkspace(null);
      setSubscription(null);
      setPhase("login");
      setLoginError(canonical);
      return canonical;
    }
    if (
      scope?.modelId !== null &&
      scope?.modelId !== undefined &&
      isConfigurationInvalidationError(canonical)
    ) {
      void reconcileConfigurationInvalidation(canonical, {
        chatId: scope.chatId,
        modelId: scope.modelId,
      });
      return canonical;
    }
    setPageError(canonical);
    return canonical;
  }

  if (phase === "booting") {
    return <LoadingScreen />;
  }
  if (phase === "login") {
    return <Login error={loginError} onLogin={login} pending={loginPending} />;
  }
  if (phase === "unavailable" || workspace === null) {
    return <UnavailableScreen error={pageError} onRetry={retryBoot} />;
  }

  const latestStatus = activeChat === null ? null : latestCanonicalStatus(activeChat.timeline);

  return (
    <main className="app-shell">
      <Sidebar
        activeChatId={activeChat?.chatId ?? null}
        chats={workspace.chats}
        creating={creating}
        hasMoreChats={workspace.chatsNextBefore !== null}
        loadingMoreChats={loadingMoreChats}
        models={workspace.models}
        navigationPending={loadingChat}
        onCreateChat={createChat}
        onLoadMoreChats={loadMoreChats}
        onSelectChat={selectChat}
        onSelectModel={selectModel}
        selectedModelId={selectedModelId}
      />
      <section className="chat-pane" aria-label="Active conversation">
        {activeChat === null ? (
          <EmptyConversation
            error={pageError ?? streamError}
            hasModels={workspace.models.length > 0}
          />
        ) : (
          <>
            <header className="chat-header">
              <div>
                <span className="chat-header-label">Fan ID</span>
                <h1>{activeChat.fanId}</h1>
              </div>
              <CompactStatus
                connection={connection}
                error={pageError ?? streamError}
                status={latestStatus}
              />
              {activeChat.readOnly ? (
                <span className="read-only-indicator">
                  read-only · {activeChat.readinessReason}
                </span>
              ) : null}
            </header>
            <Timeline
              actionsDisabled={activeChat.readOnly}
              chat={activeChat}
              loadingEarlier={loadingEarlier}
              onLoadEarlier={loadEarlierMessages}
              onPurchase={purchase}
              pendingOfferId={pendingOfferId}
            />
            <Composer
              disabled={activeChat.readOnly || pendingOfferId !== null}
              onSend={sendMessage}
              pending={sending}
            />
          </>
        )}
      </section>
    </main>
  );
}

function visibleActiveChat(
  workspace: WorkspaceView | null,
  selectedModelId: string | null,
): ChatView | null {
  const active = workspace?.activeChat;
  return active?.modelId === selectedModelId ? active : null;
}

function chooseModelId(
  workspace: WorkspaceView,
  preferred: string | undefined,
  current: string | null,
): string | null {
  const available = new Set(workspace.models.map((model) => model.modelId));
  if (preferred !== undefined && available.has(preferred)) return preferred;
  if (workspace.activeChat !== null && available.has(workspace.activeChat.modelId)) {
    return workspace.activeChat.modelId;
  }
  if (current !== null && available.has(current)) return current;
  return workspace.models[0]?.modelId ?? null;
}

function scopedAttemptKey(scopeId: string, value: string): string {
  return JSON.stringify([scopeId, value]);
}

function isSessionExpiredError(error: CanonicalError): boolean {
  return error.category === "auth" && error.code === "authentication_failed";
}

function LoadingScreen() {
  return (
    <main className="centered-screen" role="status">
      <div className="loading-mark" aria-hidden="true">LB</div>
      <p>Opening Dev Chat…</p>
    </main>
  );
}

function UnavailableScreen({
  error,
  onRetry,
}: {
  error: CanonicalError | null;
  onRetry: () => Promise<void>;
}) {
  return (
    <main className="centered-screen">
      <section className="unavailable-card" aria-labelledby="unavailable-title">
        <span className="eyebrow">LoveBomber Platform</span>
        <h1 id="unavailable-title">Dev Chat unavailable</h1>
        <p>{error?.message ?? "The workspace could not be loaded."}</p>
        {error ? <code>{error.code}</code> : null}
        <button className="primary-button" type="button" onClick={() => void onRetry()}>Retry</button>
      </section>
    </main>
  );
}

function EmptyConversation({
  error,
  hasModels,
}: {
  error: CanonicalError | null;
  hasModels: boolean;
}) {
  return (
    <div className="empty-conversation">
      <div className="empty-conversation__glyph" aria-hidden="true">↗</div>
      <h1>{hasModels ? "Choose or create a fan chat" : "No dev models available"}</h1>
      <p>
        {hasModels
          ? "Every message and demo purchase travels through the full platform connector flow."
          : "Only models carrying the dev tag are exposed to this service."}
      </p>
      {error ? (
        <CompactStatus connection="connected" error={error} status={null} />
      ) : null}
    </div>
  );
}
